/**
 * Personnel Records Utilities
 *
 * Centralized definitions and utility functions for personnel record management.
 * This module provides constants, validation functions, and formatters used across
 * personnel record components.
 */

/**
 * Personnel Status Options - Master List
 * Single source of truth for all personnel statuses
 * Each status includes: text, value, color, sortKey, and availability flags
 */
export const PERSONNEL_STATUS_OPTIONS = [
  {
    text: "Aktiv",
    value: "active",
    color: "#4CAF50",
    sortKey: 1,
    inBulkEdit: true,
  },
  {
    text: "Beurlaubt",
    value: "onLeave",
    color: "#FF9800",
    sortKey: 2,
    inBulkEdit: false,
  },
  {
    text: "Im Übertritt",
    value: "transfer",
    color: "#CDDC39",
    sortKey: 3,
    inBulkEdit: false,
  },
  {
    text: "In Bewerbung",
    value: "application",
    color: "#9E9E9E",
    sortKey: 4,
    inBulkEdit: false,
  },
  {
    text: "Extern",
    value: "external",
    color: "#03A9F4",
    sortKey: 5,
    inBulkEdit: false,
  },
  {
    text: "Betätigungsverbot",
    value: "banned",
    color: "#795548",
    sortKey: 6,
    inBulkEdit: false,
  },
  {
    text: "Passiv",
    value: "passive",
    color: "#9E9E9E",
    sortKey: 7,
    inBulkEdit: true,
  },
  {
    text: "Ehrenmitglied",
    value: "honorary",
    color: "#9C27B0",
    sortKey: 8,
    inBulkEdit: true,
  },
  {
    text: "Fördermitglied",
    value: "supporting",
    color: "#00BCD4",
    sortKey: 9,
    inBulkEdit: true,
  },
  {
    text: "Ausgetreten",
    value: "resigned",
    color: "#795548",
    sortKey: 10,
    inBulkEdit: false,
  },
  {
    text: "Entlassen",
    value: "dismissed",
    color: "#F44336",
    sortKey: 11,
    inBulkEdit: false,
  },
  {
    text: "Verstorben",
    value: "deceased",
    color: "#9E9E9E",
    sortKey: 12,
    inBulkEdit: false,
  },
  // {
  //   text: "Ausgetreten",
  //   value: "left",
  //   color: "#795548",
  //   sortKey: 13,
  //   inBulkEdit: true,
  // },
];

/**
 * Salutation Options
 */
export const SALUTATION_OPTIONS = [
  { text: "Herr", value: "Herr" },
  { text: "Frau", value: "Frau" },
];

/**
 * Letter Salutation Options
 */
export const LETTER_SALUTATION_OPTIONS = [
  { text: "Herrn", value: "Herrn" },
  { text: "Frau", value: "Frau" },
];

/**
 * Title Options
 */
export const TITLE_OPTIONS = [
  { text: "Dr.", value: "Dr." },
  { text: "Prof.", value: "Prof." },
  { text: "Prof. Dr.", value: "Prof. Dr." },
];

/**
 * Get the display text for a status value
 * @param {string} statusValue - The status value (e.g., "active", "onLeave")
 * @returns {string} The display text for the status
 */
export function getPersonnelStatusText(statusValue) {
  const status = PERSONNEL_STATUS_OPTIONS.find((s) => s.value === statusValue);
  return status ? status.text : statusValue || "Fehler";
}

/**
 * Get the color for a status value
 * @param {string} statusValue - The status value (e.g., "active", "onLeave")
 * @returns {string} The color hex code for the status
 */
export function getPersonnelStatusColor(statusValue) {
  const status = PERSONNEL_STATUS_OPTIONS.find((s) => s.value === statusValue);
  return status ? status.color : "";
}

/**
 * Get the sort key for a status (used for grouping/sorting)
 * @param {string} statusValue - The status value
 * @returns {number} The sort key
 */
export function getPersonnelStatusSortKey(statusValue) {
  const status = PERSONNEL_STATUS_OPTIONS.find((s) => s.value === statusValue);
  return status ? status.sortKey : 999;
}

/**
 * Get the sort prefix for a status (used for grouping/sorting)
 * @param {string} statusValue - The status value
 * @returns {string} The sort prefix (1-digit number)
 */
export function getPersonnelStatusPrefix(statusValue) {
  return String(getPersonnelStatusSortKey(statusValue));
}

/**
 * Convert a Firestore Timestamp or date string to yyyy-MM-dd format
 * @param {Object|string} value - Firestore Timestamp object or date string
 * @returns {string} Date in yyyy-MM-dd format, or empty string if invalid
 */
export function convertToDateString(value) {
  if (!value) return "";

  // If it's a Firestore Timestamp object
  if (typeof value === "object" && value.seconds) {
    const date = new Date(value.seconds * 1000);
    return date.toISOString().split("T")[0];
  }

  // If it's already a string, return as-is (assuming it's already formatted)
  if (typeof value === "string") {
    return value;
  }

  return "";
}

/**
 * Convert a date value to German display format (dd.MM.yyyy)
 * @param {Object|string} value - Firestore Timestamp, yyyy-MM-dd string, or Date object
 * @returns {string} Date in dd.MM.yyyy format, or empty string if invalid
 */
export function formatDateForDisplay(value) {
  if (!value) return "";

  // If it's already a yyyy-MM-dd string, format it for display
  if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = value.split("-");
    return `${day}.${month}.${year}`;
  }

  // If it's a Firestore Timestamp object
  if (typeof value === "object" && value.seconds) {
    const date = new Date(value.seconds * 1000);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  // If it's a Date object
  if (value instanceof Date) {
    const day = String(value.getDate()).padStart(2, "0");
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const year = value.getFullYear();
    return `${day}.${month}.${year}`;
  }

  return "";
}

/**
 * Personnel Record Field Definitions
 * Used for dynamic form generation and validation
 */
export const PERSONNEL_RECORD_FIELDS = {
  personnelNumber: {
    label: "Personalnummer",
    type: "text",
    required: false,
  },
  birthdate: {
    label: "Geburtsdatum",
    type: "date",
    required: false,
  },
  birthplace: {
    label: "Geburtsort",
    type: "text",
    required: false,
  },
  division: {
    label: "Abteilung/Einheit",
    type: "select",
    required: false,
  },
  rank: {
    label: "Dienstgrad",
    type: "select",
    required: false,
  },
  dutyPosition: {
    label: "Dienststellung",
    type: "select",
    required: false,
  },
  status: {
    label: "Status",
    type: "select",
    required: false,
  },
};
